import { DepartmentPlanDeal } from "../../../model/dashboard.types";
import {
  DEAL_COLUMNS,
  DealColumnId,
  DealGroupKey,
  DEAL_GROUP_OPTIONS,
  sumDealNumericField,
} from "./dealsDetailsColumns";
import { DealTableRow } from "./useDealsDetailsView";

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function dealCellValue(columnId: DealColumnId, deal: DepartmentPlanDeal): string | number {
  switch (columnId) {
    case "deal":
      return deal.link;
    case "client":
      return deal.client ?? "—";
    case "opportunity":
      return deal.opportunity;
    case "profit":
      return deal.profit;
    case "marginality":
      return deal.marginality ?? "—";
    case "actual_paid":
      return deal.actual_paid;
    case "stage_id":
      return deal.stage_id;
    case "responsible":
      return deal.responsible;
    default:
      return "—";
  }
}

function groupOptionLabel(groupKey: DealGroupKey): string {
  return DEAL_GROUP_OPTIONS.find((option) => option.id === groupKey)?.label ?? groupKey;
}

function cellXml(value: string | number): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return `<Cell><Data ss:Type="Number">${value}</Data></Cell>`;
  }
  return `<Cell><Data ss:Type="String">${escapeXml(String(value))}</Data></Cell>`;
}

function sanitizeFileName(name: string): string {
  return name.replace(/[\\/:*?"<>|]+/g, "_").trim() || "deals";
}

function downloadExcelXml(xml: string, fileName: string): void {
  const blob = new Blob([`\uFEFF${xml}`], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${sanitizeFileName(fileName)}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function wrapWorkbook(headerRow: string, bodyRows: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Worksheet ss:Name="Сделки">
  <Table>
   ${headerRow}
   ${bodyRows}
  </Table>
 </Worksheet>
</Workbook>`;
}

/** Выгрузка текущего вида детализации (с группировкой и видимыми столбцами) в Excel */
export function exportDealsDetailsToExcel(params: {
  title: string;
  columns: DealColumnId[];
  rows: DealTableRow[];
}): void {
  const { title, columns, rows } = params;
  const columnDefs = DEAL_COLUMNS.filter((column) => columns.includes(column.id));
  const headerRow = `<Row>${columnDefs.map((column) => cellXml(column.label)).join("")}</Row>`;

  const bodyRows = rows
    .map((row) => {
      if (row.type === "group") {
        const totals = {
          opportunity: sumDealNumericField(row.deals, "opportunity"),
          profit: sumDealNumericField(row.deals, "profit"),
          actual_paid: sumDealNumericField(row.deals, "actual_paid"),
        };

        return `<Row>${columnDefs
          .map((column, columnIndex) => {
            if (columnIndex === 0) {
              const indent = "  ".repeat(row.level);
              return cellXml(
                `${indent}${groupOptionLabel(row.groupKey)}: ${row.label} (${row.deals.length})`
              );
            }
            if (column.numeric && !column.noGroupSum) {
              const value =
                column.id === "opportunity"
                  ? totals.opportunity
                  : column.id === "profit"
                    ? totals.profit
                    : totals.actual_paid;
              return cellXml(value);
            }
            return cellXml("—");
          })
          .join("")}</Row>`;
      }

      return `<Row>${columnDefs
        .map((column) => cellXml(dealCellValue(column.id, row.deal)))
        .join("")}</Row>`;
    })
    .join("");

  downloadExcelXml(wrapWorkbook(headerRow, bodyRows), title);
}
